/**
 * Core: Canvas functions
 * -----------------------------------------------------------------------------
 * Functions used when setting up Canvas and storybook.
 *
 */
import { createStore } from 'vuex'
import createPersistedState from 'vuex-persistedstate'
import ClickOutside from '@we-make-websites/click-outside'

import ComponentStates from '~/core/plugins/component-states'
import Helpers from '~/core/plugins/helpers'
import SectionEvents from '~/core/plugins/section-events'
import classes from '~/config/classes'

/**
 * Get global components.
 * - Imports all .vue components in `./src/scripts/components`.
 * - Context set to `true` so it looks in sub-directories.
 * - https://github.com/bencodezen/vue-enterprise-boilerplate/blob/main/src/components/_globals.js
 * @param {Object} app - Vue app object.
 */
export function getGlobalComponents(app) {
  const globalComponents = require.context(
    '~global/',
    true,
    /.vue$/,
  )

  globalComponents.keys().forEach((filename) => {
    const componentConfig = globalComponents(filename)

    const componentName = filename
      .split('/').reverse()[0]
      .replace(/^\.\//, '')
      .replace(/\.\w+$/, '')

    app.component(componentName, componentConfig.default || componentConfig)
  })
}

/**
 * Get Vuex stores.
 * - Imports all stores in the `scripts/stores` folder.
 * - https://github.com/uicrooks/shopify-foundation-theme/blob/master/src/main.js
 */
function getVuexStores() {
  const vuexModules = require.context(
    '~stores/',
    true,
    /.js$/,
  )

  const modules = {}

  vuexModules.keys().forEach((key) => {
    const name = key.replace(/\.(\/|js)/g, '').replace(/\s/g, '-')
    modules[name] = vuexModules(key).default
  })

  /**
   * Persists state in localStorage.
   * - Manually add states to persist.
   * - See https://github.com/robinvdvleuten/vuex-persistedstate#customize-storage
   *   for instructions on using sessionStorage or cookies instead.
   */
  const plugins = []

  if (!cnvs.settings.disableVuexPersist) {
    plugins.push(
      createPersistedState({
        key: 'cnvs',
        paths: [
          'cart.response',
          'market-switcher.geolocation',
          'market-switcher.preference',
          'overlays.dismissed',
        ],
      }),
    )
  }

  return createStore({
    modules,
    plugins,
  })
}

/**
 * Set Canvas Vue plugins.
 * @param {Object} app - Vue app object.
 */
export function setVuePlugins(app) {
  app.use(ClickOutside)
  app.use(ComponentStates)
  app.use(Helpers)

  if (!cnvs.storybook) {
    app.use(SectionEvents)
  }

  app.use(getVuexStores())

  /**
   * Add a custom directive for trigger icon animations on parent interaction
   */
  app.directive("trigger-icon", {
    mounted(el) {
      if (window.innerWidth <= 768){
        return
      }

      ['mouseenter', 'mouseleave'].forEach(event => {
        el.addEventListener(event, () => {
          const icons = [...el.querySelectorAll('.animated-icon__icon')]

          icons.forEach(icon => {
            icon.classList.toggle(classes.activeIcon)
          })
        })
      })
    },
  })
}
